import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import LogoIcon from '@/assets/images/logo-icon.svg?react'
import TeamLogo from '@/assets/images/team-logo.svg?react'
import { message } from '@/components'
import { Button, Input, Trans } from '@/components'
import { MfaChallengeModal } from '@/components/mfa/MfaChallengeModal'
import { LoginScope } from '@/consts/team'
import { useAuthStore } from '@/stores/auth'
import { useTeamTokenData } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { isTeamTokenExpired } from '@/utils/auth'
import { getErrorCodeMessage } from '@/utils/error'

import { ForgotTeamPasswordModal } from './ForgotTeamPasswordModal'
import { InitPasswordModal } from './InitPasswordModal'

export function TeamLogin() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const authStore = useAuthStore()
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const teamTokenData = useTeamTokenData()
  const [showInitPasswordModal, setShowInitPasswordModal] = useState(false)
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false)
  const [showMfaModal, setShowMfaModal] = useState(false)
  const [mfaChallenge, setMfaChallenge] = useState('')

  // Redirect to team page if already logged in with valid team token
  useEffect(() => {
    if (teamTokenData && !isTeamTokenExpired()) {
      // User already has valid team session, redirect to team dashboard
      navigate('/team/home', { replace: true })
    }
  }, []) // Only run once on mount, don't react to changes

  const handleLoginSuccess = useCallback(() => {
    const suggestion = useUserStore.getState().suggestion
    if (suggestion.needUpdatePassword) {
      setShowInitPasswordModal(true)
    } else {
      navigate('/team/home')
    }
  }, [navigate])

  const onLogin = useCallback(() => {
    if (loading) {
      return
    }
    setLoading(true)
    authStore
      .loginStep1(email, password, LoginScope.Team)
      .then((result) => {
        if (result.mfaRequired) {
          setMfaChallenge(result.mfaChallenge)
          setShowMfaModal(true)
          return
        }
        return authStore
          ._finalizeLogin(result.token, email, LoginScope.Team, true)
          .then(() => handleLoginSuccess())
      })
      .finally(() => {
        setLoading(false)
      })
  }, [authStore, email, password, loading, handleLoginSuccess])

  const handleMfaExpired = () => {
    setShowMfaModal(false)
    setMfaChallenge('')
    message.error(getErrorCodeMessage(240, t))
  }

  const handlePasswordChangeSuccess = useCallback(() => {
    setShowInitPasswordModal(false)
    navigate('/team/home')
  }, [navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-[image:var(--image-app-background)] bg-cover bg-center bg-no-repeat">
      <div className="flex flex-col items-center gap-y-8 md:px-6">
        <div className="card bg-panel-white w-[480px] md:w-full flex flex-col items-center gap-4">
          <div className="w-full py-2 flex flex-col items-center gap-4">
            <LogoIcon className="auth-logo-icon w-16 h-16" />
            <h2 className="text-primary h5 text-center flex items-center gap-2">
              <Trans i18nKey="teamLogin.title">Welcome To Glows.ai</Trans>
              <TeamLogo className="h-6 w-[50px]" />
            </h2>

            <div className="w-full text-primary">
              <div className="mb-2 t-sb">
                <Trans i18nKey="teamLogin.labelAccount">Account</Trans>
              </div>
              <Input
                placeholder="Account"
                value={email}
                disabled={loading}
                className="t-x-sb"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="w-full text-primary">
              <div className="mb-2 t-sb">
                <Trans i18nKey="teamLogin.labelPassword">Password</Trans>
              </div>
              <Input
                placeholder="Password"
                type="password"
                value={password}
                disabled={loading}
                className="t-x-sb"
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onLogin()
                  }
                }}
              />
            </div>

            <Button type="secondary" block className="mt-2" onClick={onLogin} disabled={loading}>
              <Trans i18nKey="teamLogin.loginBtn">Login</Trans>
            </Button>

            <div className="flex mt-4 t-lg md:t-sm gap-2">
              <button
                type="button"
                className="text-btn-secondary font-bold cursor-pointer hover:underline"
                onClick={() => setShowForgotPasswordModal(true)}
              >
                <Trans i18nKey="teamLogin.forgotPassword">forgot password?</Trans>
              </button>
            </div>
          </div>
        </div>
        <div className="max-w-[480px] text-center t-sm text-secondary">
          <Trans i18nKey="teamLogin.agreement">
            By registering on our platform means that you have read and agree to our
            <a className="text-primary" href="https://glows.ai/eula">
              EULA
            </a>
            ,
            <a className="text-primary" href="https://glows.ai/tos">
              Terms of Service
            </a>
            and
            <a className="text-primary" href="https://glows.ai/privacy">
              Privacy Policy
            </a>
          </Trans>
        </div>
      </div>

      {/* Init Password Modal - mandatory, cannot be dismissed without changing password */}
      {showInitPasswordModal && <InitPasswordModal onSuccess={handlePasswordChangeSuccess} />}

      {/* Forgot Password Modal */}
      <ForgotTeamPasswordModal
        open={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />

      <MfaChallengeModal
        open={showMfaModal}
        mfaChallenge={mfaChallenge}
        email={email}
        scope={LoginScope.Team}
        onSuccess={() => {
          setShowMfaModal(false)
          setMfaChallenge('')
          handleLoginSuccess()
        }}
        onBack={() => {
          setShowMfaModal(false)
          setMfaChallenge('')
        }}
        onExpired={handleMfaExpired}
      />
    </div>
  )
}
